import { Skeleton, Stack } from '@mui/material'
import { APIError } from 'components/core/errors/api'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { SearchManagedUsersARSingleSelect, SelectOneUserHook } from 'components/core/search/users'
import { createFormStyles } from 'components/pages/accessrequest/create/form/styles'
import { AccessRequest } from 'components/pages/accessrequest/create/form-v2/RequestAccessFormV2'
import { UserValue } from 'lib/actions/user'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { SearchManagedUsersRequest, UserStatus } from 'pbts/c1/api/user/v1/user'
import { FC, useContext, useMemo } from 'react'
import { Control, Controller } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { managedUserSearchForAccessRequestsApi } from 'redux_toolkit/users'

type AccessRequestFormUserFieldProps = {
    control: Control<AccessRequest>,
    autoFocus?: boolean
}

const roles = [RoleID.SuperAdmin, RoleID.AccessRequestHelpdesk, RoleID.AccessRequestAdmin] as const

export const AccessRequestFormUserField: FC<AccessRequestFormUserFieldProps> = ({
    control,
    autoFocus,
}) => {
    const userContext = useContext(UserContext)
    userContext.waitForReady()
    const intl = useIntl()

    const canSelectAnyUser = roles.some(
        (roleId) => userContext.user.roleIds.includes(roleId),
    )

    const managerId = userContext.user.id
    const {
        data: searchManagedUsersResponse,
        isLoading: isLoadingSearchManagedUsers,
        isError: isErrorSearchManagedUsers,
        error: searchManagedUsersError,
    } = managedUserSearchForAccessRequestsApi.useSearchManagedUsersForAccessRequests(SearchManagedUsersRequest.create({
        managerId,
    }), {
        actionable: !canSelectAnyUser,
    })

    const isUserManager = useMemo(
        () => !canSelectAnyUser && searchManagedUsersResponse && searchManagedUsersResponse.flat(1).length > 0,
        [canSelectAnyUser, searchManagedUsersResponse],
    )

    const SelectComponent = useMemo(() => {
        if (!isUserManager && !canSelectAnyUser) {
            return null
        }

        return <>
            <C1FormLabel
                label={intl.formatMessage({
                    id: 'component_access_request_user_input_label',
                    defaultMessage: 'Who needs access?',
                })}
                htmlFor="user"
            />
            <Controller
                name="user"
                control={control}
                render={({ field }): React.ReactElement =>
                    (canSelectAnyUser
                        ? <SelectOneUserHook
                            autoFocus={autoFocus}
                            data-testid="access-request-user-select"
                            id="user"
                            hideLabel
                            onSelected={(v: UserValue | null): void => {
                                field.onChange(v)
                            }}
                            baseRequest={{ userStatuses: [UserStatus.ENABLED] }}
                            onBlur={field.onBlur}
                            value={field.value}
                        />
                        : <SearchManagedUsersARSingleSelect
                            autoFocus={autoFocus}
                            data-testid="access-request-user-select"
                            id="user"
                            onSelected={(v: UserValue | null): void => {
                                field.onChange(v)
                            }}
                            baseRequest={{ userStatuses: [UserStatus.ENABLED] }}
                            value={field.value}
                            managerId={managerId}
                            onBlur={field.onBlur}
                        />
                    )
                }
            />
        </>
    }, [canSelectAnyUser, isUserManager, control, managerId, autoFocus, intl])

    if (isErrorSearchManagedUsers && searchManagedUsersError) {
        return <APIError err={searchManagedUsersError} />
    }

    if (!canSelectAnyUser && isLoadingSearchManagedUsers && !searchManagedUsersResponse?.[0]?.length) {
        return <Stack spacing={1}>
            <Skeleton sx={createFormStyles.small} />
            <Skeleton sx={createFormStyles.realBig} />
        </Stack>
    }

    return SelectComponent
}
